import { VisitTypeDto } from '@lmt/shared-types';
import {
  DXRG,
  Table,
  useReactDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid';
import { useReactDataGridLocalization } from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid-localization';
import { TableDotMenu } from '@lmt/unicorn-shared/app/components/common/table/TableDotMenu';
import { secondsToTimeString } from '@lmt/unicorn-shared/app/helpers/date-helper';
import {
  FullHeightGridRoot,
  GetStyledRow,
  HideNullTitlesHeaderContent,
  HoverableTable,
  StickyHeaderRow,
} from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { Paper } from '@mui/material';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  data: VisitTypeDto[];
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
};

const TableKeys: { [dictionary: string]: string } = {
  value: 'value',
  startTime: 'start_time',
  duration: 'duration',
  option: 'option',
};

export const VisitTypeTable: FC<Props> = (props) => {
  const i18nextDataBlock = useScopedTranslation(
    'dictionaries',
    NS_DataBlockFields,
  );
  const i18nextGlobal = useTranslation();

  useReactDataGrid();
  const localizationMessages = useReactDataGridLocalization();

  const columns = useMemo(
    () => [
      {
        name: TableKeys.value,
        title: i18nextDataBlock.t(
          `visit_types.columns.${TableKeys.value}` as any,
        ),
        getCellValue: (row: VisitTypeDto) => i18nextGlobal.t(row.value as any),
      },
      {
        name: TableKeys.startTime,
        title: i18nextDataBlock.t(
          `visit_types.columns.${TableKeys.startTime}` as any,
        ),
        getCellValue: (row: VisitTypeDto) =>
          secondsToTimeString(row.specificStartTimeInSecondsSinceMidnight),
      },
      {
        name: TableKeys.duration,
        title: i18nextDataBlock.t(
          `visit_types.columns.${TableKeys.duration}` as any,
        ),
        getCellValue: (row: VisitTypeDto) =>
          row.defaultDurationInSeconds / 3600,
      },
      {
        name: TableKeys.option,
        title: '',
        getCellValue: (row: VisitTypeDto) => (
          <div
            onClick={(event: any) => event.stopPropagation()}
            style={{ margin: -6 }}
          >
            <TableDotMenu
              options={[
                {
                  title: i18nextDataBlock.t('list.options.delete_button'),
                  onClick: () => props.onDelete(row.id),
                  isDeleteItem: true,
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [],
  );

  const tableColumnExtensions = useMemo<Table.ColumnExtension[]>(
    () => [{ columnName: TableKeys.option, width: '60px' }],
    [],
  );

  return (
    <Paper style={{ height: '100%', width: '100%' }}>
      <DXRG.Grid
        columns={columns}
        rows={props.data}
        rootComponent={FullHeightGridRoot}
      >
        <DXRG.SortingState columnSortingEnabled={false} />
        <DXRG.IntegratedSorting />
        <DXRG.Table
          messages={localizationMessages}
          tableComponent={HoverableTable}
          columnExtensions={tableColumnExtensions}
          rowComponent={GetStyledRow((row) => props.onEdit(row.id))}
        />
        <DXRG.TableHeaderRow
          showSortingControls
          contentComponent={HideNullTitlesHeaderContent}
          rowComponent={StickyHeaderRow}
        />
      </DXRG.Grid>
    </Paper>
  );
};
